import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

const RELOAD_KEY = 'barnamegram:lazy-import-reloaded';

function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return /(?:dynamically imported module|importing a module script failed|loading chunk|fetch dynamically imported module)/i.test(
    message,
  );
}

/**
 * React.lazy recovery for deployments where an entry file and its chunks are
 * briefly out of sync (or a transient request fails). The importer is retried
 * once, then the page is reloaded at most once per tab so it can obtain the
 * current entry file. A second failure is left for ErrorBoundary to display.
 */
export function lazyWithRetry<T extends ComponentType<object>>(
  importer: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      const module = await importer();
      try {
        sessionStorage.removeItem(RELOAD_KEY);
      } catch {
        // Storage may be disabled; a successful import should still render.
      }
      return module;
    } catch (firstError) {
      if (!isChunkLoadError(firstError)) throw firstError;

      // A short retry handles transient network failures without disrupting
      // the current route.
      await new Promise((resolve) => window.setTimeout(resolve, 250));
      try {
        const module = await importer();
        try {
          sessionStorage.removeItem(RELOAD_KEY);
        } catch {
          // Storage may be disabled; a successful import should still render.
        }
        return module;
      } catch (retryError) {
        if (!isChunkLoadError(retryError)) throw retryError;

        try {
          if (!sessionStorage.getItem(RELOAD_KEY)) {
            sessionStorage.setItem(RELOAD_KEY, '1');
            window.location.reload();
          }
        } catch {
          // Continue to ErrorBoundary when reload/storage is unavailable.
        }
        throw retryError;
      }
    }
  });
}
